/* eslint-disable complexity */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
 Alert, Hint, Label, Tooltip,
} from '@americanexpress/dls-react';
import FxToolTipInfo from '../../FxToolTipInfo';
import { FxCustomAlert } from '../../FxAlert';
import { mapJustifyContentTypeToClassName, mapflexDirectionTypeToClassName } from '../../../constants/styles';
import { FxAdditionalButton } from '../../FxButtons/FxAdditionalButton';

export const FxFormItem = ({
  id,
  label,
  className,
  isRequired,
  errorMessage,
  children,
  additionalText,
  onAdditionalTextClick,
  additionalTexts,
  toolTipProps,
  showError,
  showCustomError,
  showErrorModal,
  errorModalLabel,
  isDisabled, // @deprecated, do not use, only FxToggleField uses this prop
  disabled, // required to disable the label field.
  labelAndAdditionalTextFlexDirection,
  hint,
  labelTheme,
}) => {
  const { justifyButtons = '', flexDirection = '', additionaltextData = [] } = additionalTexts || {};
  const [containerElement, setContainerElement] = useState(null);
  const [labelElement, setLabelElement] = useState(null);
  const [labelStyles, setLabelStyles] = useState({});
  const [isTruncateLabel, setIsTruncateLabel] = useState(false);

  const truncateLabel = (containerEl, labelEl) => containerEl && labelEl
        && labelEl.clientWidth > containerEl.clientWidth;

  useEffect(() => {
    const cntnrElement = document.getElementById('label-container');
    const lblElement = document.getElementById(`${id}-Label`);
    setContainerElement(cntnrElement);
    setLabelElement(lblElement);
    setIsTruncateLabel(truncateLabel(cntnrElement, lblElement));
  }, []);

  useEffect(() => {
    if (isTruncateLabel) {
      const lblTheme = {
        // as info icon and mandatory label are not getting truncated, keeping some width to the label to display them
        maxWidth: `${containerElement.clientWidth - 25}px`,
        maxHeight: '24px',
        '-webkit-box-orient': 'vertical',
        display: '-webkit-box',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '-webkit-line-clamp': '1',
        };
        setLabelStyles(lblTheme);
    }
  }, [containerElement, labelElement, isTruncateLabel]);

  /**
   * added left marging for tool tip and moved * (required symbol) to left to fix formatting issue as part of US5498755
   */
  return (
    <div className={`${className}`}>
      {label && (
        <div
          style={{ gap: '15px' }}
          className={`flex ${
            additionalTexts
            && mapflexDirectionTypeToClassName[flexDirection]
          } ${additionalText && mapflexDirectionTypeToClassName[labelAndAdditionalTextFlexDirection]} flex-justify-between`}
          id="label-container"
        >
          <div className="flex flex-align-center" style={{ minWidth: 'fit-content' }}>
            <div>
              {isTruncateLabel ? (
                <Tooltip
                  id="label-tooltip"
                  triggerElement={(
                    <Label htmlFor={id} id={`${id}-Label`} theme={{ ...labelTheme, ...labelStyles }}>
                      {label}
                    </Label>

                )}
                >
                  {label} {isRequired ? ' *' : ''}
                </Tooltip>
              ) : (
                <Label htmlFor={id} id={`${id}-Label`} theme={{ ...labelTheme, ...labelStyles }} disabled={isDisabled || disabled}> {/* disable the label field as well if input field is also disabling */}
                  {label} {isRequired ? ' *' : ''}
                </Label>
              )}

            </div>
            {isRequired && isTruncateLabel ? ' *' : ''}
            {toolTipProps && (
            <FxToolTipInfo {...toolTipProps} theme={{ margin: '-11px' }} />
      )}
            {/* <RenderLabelAdditonalContent isMandatory={isRequired} helpInfoIconProps={toolTipProps} /> */}
          </div>
          {additionalTexts && additionaltextData && (
            <div className={`width-full flex flex-align-center ${mapJustifyContentTypeToClassName[justifyButtons]}`}>
              {additionaltextData.map((data) => <FxAdditionalButton key={data.additionalText} {...data} />)}
            </div>
          )}
          {additionalText && onAdditionalTextClick && (
            <span
              className={
                isDisabled ? 'disabled' : 'dls-bright-blue icon-link icon-hover'
              }
              tabIndex="0"
              role="button"
              onKeyPress={isDisabled ? null : onAdditionalTextClick}
              onClick={isDisabled ? null : onAdditionalTextClick}
            >
              {additionalText}
            </span>
          )}
        </div>
      )}
      {hint && <Hint id={`${id}-Hint`}>{hint}</Hint>}
      {children}
      {!showCustomError && showError && errorMessage && (
        <Alert id={`${id}-Alert`} status="error">
          {errorMessage}
        </Alert>
      )}
      {showCustomError && errorMessage && (
        <FxCustomAlert
          errorMessage={errorMessage}
          errorModalLabel={errorModalLabel}
          showErrorModal={showErrorModal}
        />
      )}
    </div>
  );
};

FxFormItem.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node,
  className: PropTypes.string,
  isRequired: PropTypes.bool,
  errorMessage: PropTypes.string,
  children: PropTypes.node,
  additionalTexts: PropTypes.shape({
    justifyButtons: PropTypes.bool,
    flexDirection: PropTypes.string,
    additionaltextData: PropTypes.arrayOf(PropTypes.shape({
      additionalText: PropTypes.string,
      additionalTextTheme: PropTypes.string,
      onAdditionalTextClick: PropTypes.func,
      isDisabled: PropTypes.bool,
    })),
  }),
  additionalText: PropTypes.string,
  onAdditionalTextClick: PropTypes.func,
  toolTipProps: PropTypes.shape({}),
  showError: PropTypes.bool,
  showCustomError: PropTypes.bool,
  showErrorModal: PropTypes.func,
  errorModalLabel: PropTypes.string,
  isDisabled: PropTypes.bool,
  // eslint-disable-next-line react/boolean-prop-naming
  disabled: PropTypes.bool,
  labelAndAdditionalTextFlexDirection: PropTypes.string,
  hint: PropTypes.string,
  labelTheme: PropTypes.shape({}),
};

FxFormItem.defaultProps = {
  label: null,
  className: null,
  isRequired: false,
  errorMessage: null,
  children: null,
  additionalText: null,
  onAdditionalTextClick: null,
  toolTipProps: null,
  showError: true,
  showCustomError: false,
  showErrorModal: () => {},
  errorModalLabel: '',
  labelAndAdditionalTextFlexDirection: null,
  hint: null,
  labelTheme: {},
  disabled: false,
};
